/**
 * Shares one active asynchronous operation among concurrent callers.
 *
 * Completion, retry, and lifecycle policy belong to the caller. A rejected operation is cleared
 * just like a fulfilled operation, so a later call can start new work.
 */
export class AsyncSingleFlight<Result = void> {
    #active: Promise<Result> | undefined

    public get active(): Promise<Result> | undefined {
        return this.#active
    }

    public async run(operation: () => Promise<Result>): Promise<Result> {
        if (this.#active !== undefined) {
            return await this.#active
        }

        // Defer invocation until after #active is assigned so a synchronously reentrant caller
        // observes and joins this flight instead of starting a second operation.
        const active = Promise.resolve().then(operation)
        this.#active = active

        try {
            return await active
        } finally {
            if (this.#active === active) {
                this.#active = undefined
            }
        }
    }
}
